import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, Wallet, Building, TrendingUp, Users, Calendar, ChevronLeft, ChevronRight, Copy, Save, ArrowUp, ArrowDown } from "lucide-react";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

import { API } from '../utils/api';

import { CreditCard } from "lucide-react";

const ACCOUNT_TYPES = [
  { value: "savings", label: "Savings", icon: Wallet },
  { value: "investment", label: "Investment", icon: TrendingUp },
  { value: "checking", label: "Checking", icon: Building },
  { value: "credit_card", label: "Credit Card", icon: CreditCard },
];

const CATEGORIES = [
  { value: "personal", label: "Personal" },
  { value: "child", label: "Child's Account" },
  { value: "joint", label: "Joint Account" },
  { value: "business", label: "Business" },
];

const CURRENCIES = [
  "EUR", "USD", "GBP", "JPY", "CHF", "CAD", "AUD", "NZD",
  "SEK", "NOK", "DKK", "PLN", "CZK", "HUF", "RON", "BGN", "HRK", "ISK",
  "CNY", "HKD", "SGD", "TWD", "KRW", "INR", "IDR", "MYR", "PHP", "THB", "VND", "PKR", "BDT", "LKR",
  "AED", "SAR", "QAR", "KWD", "BHD", "OMR", "ILS", "TRY", "EGP", "ZAR", "NGN", "KES", "MAD",
  "MXN", "BRL", "ARS", "CLP", "COP", "PEN", "UYU",
  "RUB", "UAH", "BTC", "ETH"
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const formatCurrency = (value, currency = "EUR") => {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 2,
  }).format(value);
};

const getAccountIcon = (type) => {
  const accountType = ACCOUNT_TYPES.find(t => t.value === type);
  return accountType?.icon || Wallet;
};

export default function Accounts({ user }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [exchangeRates, setExchangeRates] = useState({});
  const [monthLoading, setMonthLoading] = useState(true);
  
  // Monthly balance state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [monthlyBalances, setMonthlyBalances] = useState({});
  const [previousBalances, setPreviousBalances] = useState({});
  const [differences, setDifferences] = useState({});
  const [availableMonths, setAvailableMonths] = useState([]);
  const [editedBalances, setEditedBalances] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    account_type: "savings",
    balance: "",
    currency: user?.default_currency || "EUR",
    category: "personal",
    owner_name: "",
    annual_return_percent: "0",
  });

  const defaultCurrency = user?.default_currency || "EUR";

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCurrency]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchMonthlyBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth]);

  const fetchData = async () => {
    try {
      const [accountsRes, ratesRes, monthsRes] = await Promise.all([
        axios.get(`${API}/accounts?year=${selectedYear}&month=${selectedMonth}`),
        axios.get(`${API}/exchange-rates?base=${defaultCurrency}`),
        axios.get(`${API}/monthly-balances/available-months`)
      ]);
      setAccounts(accountsRes.data);
      setExchangeRates(ratesRes.data.rates || {});
      setAvailableMonths(monthsRes.data || []);
      
      // If no months available and accounts exist, initialize Jan 2026 with current balances
      if (monthsRes.data.length === 0 && accountsRes.data.length > 0) {
        try {
          const initRes = await axios.post(`${API}/monthly-balances/initialize`);
          if (initRes.data.initialized) {
            toast.success("Initialized January 2026 with current account balances");
            // Refresh available months
            const refreshedMonths = await axios.get(`${API}/monthly-balances/available-months`);
            setAvailableMonths(refreshedMonths.data || []);
          }
        } catch (initError) {
          console.error("Error initializing balances:", initError);
        }
        setSelectedYear(2026);
        setSelectedMonth(1);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load accounts");
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyBalances = async () => {
    try {
      setMonthLoading(true);
      // Fetch new month's data WITHOUT clearing the current list — otherwise the
      // "No Accounts Yet" empty-state briefly flashes. The Total Balance uses
      // monthLoading to render a skeleton instead of stale/mixed numbers.
      const [acctRes, response] = await Promise.all([
        axios.get(`${API}/accounts?year=${selectedYear}&month=${selectedMonth}`),
        axios.get(`${API}/monthly-balances/with-diff/${selectedYear}/${selectedMonth}`),
      ]);
      setAccounts(acctRes.data || []);

      const data = response.data || {};
      const hasSaved = data.has_saved_snapshot;
      const previousBalancesData = data.previous_balances || {};

      if (hasSaved) {
        // A real snapshot exists for this month
        setMonthlyBalances(data.balances || {});
        setEditedBalances(data.balances || {});
        setPreviousBalances(previousBalancesData);
        setDifferences(data.differences || {});
      } else {
        // No snapshot for this month — seed from copy-from (nearest earlier snapshot)
        const copyRes = await axios.get(`${API}/monthly-balances/copy-from/${selectedYear}/${selectedMonth}`);
        const copiedBalances = copyRes.data || {};
        setMonthlyBalances(copiedBalances);
        setEditedBalances(copiedBalances);
        // Diff vs the previous month (or nearest earlier) so the user still
        // sees whether the seeded values differ from the last saved point.
        setPreviousBalances(previousBalancesData);
        const diffs = {};
        Object.keys(copiedBalances).forEach((accountId) => {
          const prev = previousBalancesData[accountId] || 0;
          diffs[accountId] = (copiedBalances[accountId] || 0) - prev;
        });
        setDifferences(diffs);
      }
      setHasChanges(false);
    } catch (error) {
      console.error("Error fetching monthly balances:", error);
      // Fallback to current account balances
      const currentBalances = {};
      accounts.forEach(acc => {
        currentBalances[acc.account_id] = acc.balance;
      });
      setMonthlyBalances(currentBalances);
      setEditedBalances(currentBalances);
    } finally {
      setMonthLoading(false);
    }
  };

  const handleBalanceChange = (accountId, value) => {
    const newBalances = {
      ...editedBalances,
      [accountId]: parseFloat(value) || 0
    };
    setEditedBalances(newBalances);
    setHasChanges(true);
  };

  const saveMonthlyBalances = async () => {
    try {
      const balances = Object.entries(editedBalances).map(([account_id, balance]) => ({
        account_id,
        balance
      }));
      
      await axios.post(`${API}/monthly-balances`, {
        year: selectedYear,
        month: selectedMonth,
        balances
      });
      
      toast.success(`Balances saved for ${MONTHS[selectedMonth - 1]} ${selectedYear}`);
      setHasChanges(false);
      
      // Refresh data
      const monthsRes = await axios.get(`${API}/monthly-balances/available-months`);
      setAvailableMonths(monthsRes.data || []);
      fetchMonthlyBalances();
    } catch (error) {
      console.error("Error saving balances:", error);
      toast.error("Failed to save balances");
    }
  };

  const copyFromPreviousMonth = async () => {
    let prevMonth = selectedMonth - 1;
    let prevYear = selectedYear;
    if (prevMonth < 1) {
      prevMonth = 12;
      prevYear = selectedYear - 1;
    }
    
    try {
      const response = await axios.get(`${API}/monthly-balances/copy-from/${prevYear}/${prevMonth}`);
      const copiedBalances = response.data || {};
      
      // Fill in any missing accounts
      const newBalances = { ...copiedBalances };
      accounts.forEach(acc => {
        if (!(acc.account_id in newBalances)) {
          newBalances[acc.account_id] = acc.balance;
        }
      });
      
      setEditedBalances(newBalances);
      setHasChanges(true);
      toast.success(`Copied balances from ${MONTHS[prevMonth - 1]} ${prevYear}`);
    } catch (error) {
      console.error("Error copying balances:", error);
      toast.error("Failed to copy balances");
    }
  };

  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  // Convert amount to default currency using API rates
  const convertToDefaultCurrency = (amount, fromCurrency) => {
    if (!fromCurrency || fromCurrency === defaultCurrency) return amount;
    // Exchange rates from API have lowercase keys
    const rate = exchangeRates[fromCurrency.toLowerCase()];
    if (!rate || rate === 0) return amount;
    return amount / rate;
  };

  // Calculate total balance in default currency using monthly balances
  const totalBalance = accounts.reduce((sum, acc) => {
    const balance = editedBalances[acc.account_id] ?? acc.balance;
    const converted = convertToDefaultCurrency(balance, acc.currency);
    return sum + converted;
  }, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const accountData = {
        ...formData,
        balance: parseFloat(formData.balance) || 0,
        annual_return_percent: parseFloat(formData.annual_return_percent) || 0,
      };
      
      const accountId = editingAccount?.account_id;

      if (editingAccount) {
        await axios.put(`${API}/accounts/${accountId}`, accountData);
        toast.success("Account updated");
      } else {
        // Anchor the account's start-of-life to the currently-viewed month
        await axios.post(
          `${API}/accounts?effective_year=${selectedYear}&effective_month=${selectedMonth}`,
          accountData
        );
        toast.success(`Account created from ${selectedMonth}/${selectedYear} onwards`);
      }
      
      setDialogOpen(false);
      resetForm();
      await fetchData();
      // Re-apply the historical filter for the currently-selected month
      fetchMonthlyBalances();

      // Update editedBalances AFTER fetchData completes to ensure UI shows correct balance
      if (accountId) {
        setEditedBalances(prev => ({
          ...prev,
          [accountId]: accountData.balance
        }));
      }
    } catch (error) {
      console.error("Error saving account:", error);
      toast.error("Failed to save account");
    }
  };

  const handleDelete = async () => {
    if (!accountToDelete) return;
    
    try {
      // Delete effective from the currently-viewed month onwards.
      await axios.delete(
        `${API}/accounts/${accountToDelete.account_id}?effective_year=${selectedYear}&effective_month=${selectedMonth}`
      );
      toast.success(`Account removed from ${selectedMonth}/${selectedYear} onwards`);
      setDeleteDialogOpen(false);
      setAccountToDelete(null);
      await fetchData();
      fetchMonthlyBalances();
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("Failed to delete account");
    }
  };

  const openEditDialog = (account) => {
    setEditingAccount(account);
    // Use editedBalances value (what's shown on UI) if available, otherwise use account.balance
    const displayedBalance = editedBalances[account.account_id] ?? account.balance;
    setFormData({
      name: account.name,
      account_type: account.account_type,
      balance: displayedBalance.toString(),
      currency: account.currency,
      category: account.category,
      owner_name: account.owner_name || "",
      annual_return_percent: (account.annual_return_percent || 0).toString(),
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (account) => {
    setAccountToDelete(account);
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setEditingAccount(null);
    setFormData({
      name: "",
      account_type: "savings",
      balance: "",
      currency: defaultCurrency,
      category: "personal",
      owner_name: "",
      annual_return_percent: "0",
    });
  };

  const hasMonthData = availableMonths.some(m => m.year === selectedYear && m.month === selectedMonth);

  if (loading) {
    return (
      <Layout user={user} pageTitle="Accounts">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} pageTitle="Accounts">
      <div data-testid="accounts-page" className="space-y-8">
        {/* Page Header */}
        <PageHeader pageKey="accounts" />
        
        {/* Month Selector */}
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-green-400" />
                <span className="text-gray-400">Balance as of:</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToPreviousMonth}
                  className="h-8 w-8 text-gray-400 hover:text-white"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                
                <div className="flex items-center gap-2">
                  <Select
                    value={selectedMonth.toString()}
                    onValueChange={(value) => setSelectedMonth(parseInt(value))}
                  >
                    <SelectTrigger className="w-[130px] bg-secondary border-border text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {MONTHS.map((month, index) => (
                        <SelectItem key={index} value={(index + 1).toString()} className="text-gray-300 focus:text-white focus:bg-secondary">
                          {month}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select
                    value={selectedYear.toString()}
                    onValueChange={(value) => setSelectedYear(parseInt(value))}
                  >
                    <SelectTrigger className="w-[90px] bg-secondary border-border text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map((year) => (
                        <SelectItem key={year} value={year.toString()} className="text-gray-300 focus:text-white focus:bg-secondary">
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goToNextMonth}
                  className="h-8 w-8 text-gray-400 hover:text-white"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyFromPreviousMonth}
                  className="rounded-full border-border text-gray-300 hover:text-white hover:bg-secondary"
                >
                  <Copy className="w-4 h-4 mr-1" />
                  Copy Previous
                </Button>
                
                <Button
                  onClick={saveMonthlyBalances}
                  disabled={!hasChanges}
                  className={`rounded-full font-semibold ${hasChanges ? 'bg-green-500 hover:bg-green-400 text-black' : 'bg-gray-600 text-gray-400 cursor-not-allowed'}`}
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save
                </Button>
              </div>
            </div>
            
            {!hasMonthData && (
              <p className="text-center text-amber-400 text-sm mt-3">
                No data saved for {MONTHS[selectedMonth - 1]} {selectedYear}. Edit and save to create snapshot.
              </p>
            )}
          </CardContent>
        </Card>
        
        {/* Header with total */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <p className="text-gray-400">Total Balance ({MONTHS[selectedMonth - 1]} {selectedYear})</p>
            <p
              className="text-3xl font-bold text-green-400 number-display"
              data-testid="accounts-total-balance"
            >
              {monthLoading ? (
                <span className="inline-block h-8 w-48 bg-secondary rounded animate-pulse" />
              ) : (
                formatCurrency(totalBalance, defaultCurrency)
              )}
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-green-500 hover:bg-green-400 text-black rounded-full px-6 font-semibold"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Account
          </Button>
        </div>

        {/* Accounts Grid */}
        {monthLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <Card key={i} className="bg-card border-border">
                <CardContent className="p-6">
                  <div className="h-5 w-32 bg-secondary rounded animate-pulse mb-3" />
                  <div className="h-8 w-40 bg-secondary rounded animate-pulse mb-2" />
                  <div className="h-3 w-24 bg-secondary rounded animate-pulse" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : accounts.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                <Wallet className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">No Accounts Yet</h3>
              <p className="text-gray-400 mb-6">
                Start by adding your first savings or investment account.
              </p>
              <Button
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
                className="bg-green-500 hover:bg-green-400 text-black rounded-full px-6 font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Account
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {accounts.map((account, index) => {
              const Icon = getAccountIcon(account.account_type);
              const currentBalance = editedBalances[account.account_id] ?? account.balance;
              // Only compute a diff if the account actually appears in the previous
              // snapshot. Accounts that didn't exist in the previous snapshot would
              // otherwise show their full balance as "growth".
              const accountInPrevious = account.account_id in previousBalances;
              const prevBalance = accountInPrevious ? previousBalances[account.account_id] : 0;
              const diff = accountInPrevious ? currentBalance - prevBalance : 0;
              const hasDiff = accountInPrevious;
              
              return (
                <motion.div
                  key={account.account_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="bg-card border-border hover:border-green-500/30 transition-colors">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            account.account_type === 'investment' ? 'bg-purple-500/10' :
                            account.account_type === 'checking' ? 'bg-blue-500/10' : 
                            account.account_type === 'credit_card' ? 'bg-red-500/10' : 'bg-green-500/10'
                          }`}>
                            <Icon className={`w-5 h-5 ${
                              account.account_type === 'investment' ? 'text-purple-400' :
                              account.account_type === 'checking' ? 'text-blue-400' : 
                              account.account_type === 'credit_card' ? 'text-red-400' : 'text-green-400'
                            }`} />
                          </div>
                          <div>
                            <h3 className="font-bold text-white">{account.name}</h3>
                            <p className="text-sm text-gray-500">
                              {account.category} • {account.account_type}
                            </p>
                            {account.owner_name && (
                              <p className="text-xs text-gray-600 flex items-center gap-1">
                                <Users className="w-3 h-3" /> {account.owner_name}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-500 hover:text-blue-400"
                            onClick={() => openEditDialog(account)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-500 hover:text-red-400"
                            onClick={() => openDeleteDialog(account)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Editable Balance */}
                      <div className="mb-3">
                        <Label className="text-gray-500 text-xs mb-1 block">Balance ({account.currency})</Label>
                        <Input
                          type="number"
                          value={currentBalance}
                          onChange={(e) => handleBalanceChange(account.account_id, e.target.value)}
                          className="bg-secondary border-border text-white text-lg font-bold number-display"
                        />
                      </div>
                      
                      {/* Difference from previous month */}
                      {hasDiff && (
                        <div className={`flex items-center gap-1 text-sm ${diff >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {diff >= 0 ? (
                            <ArrowUp className="w-4 h-4" />
                          ) : (
                            <ArrowDown className="w-4 h-4" />
                          )}
                          <span className="number-display">
                            {diff >= 0 ? '+' : ''}{formatCurrency(diff, account.currency)}
                          </span>
                          <span className="text-gray-500 text-xs">vs prev month</span>
                        </div>
                      )}
                      
                      {/* Annual Return */}
                      {account.annual_return_percent > 0 && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                          <TrendingUp className="w-3 h-3" />
                          <span>{account.annual_return_percent}% expected annual return</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md bg-card border-border text-white">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingAccount ? "Edit Account" : "New Account"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-gray-300">Account Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Emergency Fund"
                  required
                  className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Type</Label>
                  <Select
                    value={formData.account_type}
                    onValueChange={(value) => setFormData({ ...formData, account_type: value })}
                  >
                    <SelectTrigger className="mt-1 bg-secondary border-border text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {ACCOUNT_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value} className="text-gray-300 focus:text-white focus:bg-secondary">
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-gray-300">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger className="mt-1 bg-secondary border-border text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value} className="text-gray-300 focus:text-white focus:bg-secondary">
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-300">Owner Name (optional)</Label>
                <Input
                  value={formData.owner_name}
                  onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                  placeholder="e.g., John Doe"
                  className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Initial Balance</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.balance}
                    onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                    placeholder="0.00"
                    className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                  />
                </div>
                
                <div>
                  <Label className="text-gray-300">Currency</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => setFormData({ ...formData, currency: value })}
                  >
                    <SelectTrigger className="mt-1 bg-secondary border-border text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border max-h-[200px]">
                      {CURRENCIES.map((cur) => (
                        <SelectItem key={cur} value={cur} className="text-gray-300 focus:text-white focus:bg-secondary">
                          {cur}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-300">Expected Annual Return (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.annual_return_percent}
                  onChange={(e) => setFormData({ ...formData, annual_return_percent: e.target.value })}
                  placeholder="0"
                  className="mt-1 bg-secondary border-border text-white placeholder:text-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">Used for future planning calculations</p>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="rounded-full border-border text-gray-300 hover:text-white hover:bg-secondary"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-green-500 hover:bg-green-400 text-black rounded-full font-semibold"
                >
                  {editingAccount ? "Update" : "Create"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete Account</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400">
                Remove &quot;{accountToDelete?.name}&quot; from{" "}
                <span className="text-white font-semibold">
                  {new Date(selectedYear, selectedMonth - 1, 1).toLocaleString("en", { month: "long", year: "numeric" })}
                </span>{" "}
                onwards. Earlier months will still show this account &mdash; delete
                from those months separately if you want it gone from your entire history.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-full border-border text-gray-300 hover:text-white hover:bg-secondary">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-400 text-white rounded-full"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
